import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ChangeEvent,
  type DragEvent as ReactDragEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '@/contexts/ToastContext';
import { CloseIcon, AddIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { Button } from '@/components/ui/Button/Button';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { DropZoneOverlay } from '@/components/ui/DropZoneOverlay/DropZoneOverlay';
import { ProjectFilesClientWorkProgress } from './ProjectFilesClientWorkProgress';
import { ProjectFilesPopoverLocalDraftRow } from './ProjectFilesPopoverFileItems';
import { filterLocalDraftFilesByProjectFilesTypeFilter } from './projectFilesPopoverTypeFilterModel';
import { ProjectFilesPopoverTypeFilter } from './ProjectFilesPopoverTypeFilterMenu';
import { useProjectFilesPopoverTypeFilter } from './useProjectFilesPopoverTypeFilter';
import { dataTransferHasFiles, validateFilesForDraft, splitFileNameStemAndSuffix } from './projectFilesPopoverUtils';
import type { ProjectFilesPopoverLocalDraftProps, ProjectFileDraftItem } from './ProjectFilesPopover.types';
import { ProjectFileRenameModal } from './ProjectFileRenameModal';
import {
  isSupportedProjectMetricsFile,
  tryComputeAuthorSheetsAndPagesFromFile,
} from '@/lib/projectFileMetrics/authorSheetsCalculator';

export function ProjectFilesPopoverLocalDraft({
  open,
  onClose,
  anchorEl,
  localDraft,
}: ProjectFilesPopoverLocalDraftProps) {
  const { showError: showToastError } = useToast();
  const popoverRef = useRef<HTMLDivElement>(null);
  const fileDropBodyRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileDragHighlight, setFileDragHighlight] = useState(false);
  const osFileDragActiveRef = useRef(false);

  const [renameDraftIndex, setRenameDraftIndex] = useState<number | null>(null);
  const [appendBusy, setAppendBusy] = useState(false);
  const [appendProgress, setAppendProgress] = useState<{
    headline: string;
    fileName: string;
    percent: number | null;
  } | null>(null);

  const { files, onFilesChange, titleName, onDraftFileMarkedText } = localDraft;

  const typeFilter = useProjectFilesPopoverTypeFilter();

  const filesFiltered = useMemo(
    () => filterLocalDraftFilesByProjectFilesTypeFilter(files, typeFilter.state),
    [files, typeFilter.state]
  );

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'x',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    excludeSelector: '.context-menu, .modal-overlay',
    enabled: shouldRender && !appendBusy,
    capture: false,
    shouldIgnoreOutside: () => osFileDragActiveRef.current,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (appendBusy) return;
      if (renameDraftIndex !== null) {
        setRenameDraftIndex(null);
        return;
      }
      requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose, renameDraftIndex, appendBusy]);

  useEffect(() => {
    if (!shouldRender) return;
    const onDragEnter = (e: DragEvent) => {
      if (dataTransferHasFiles(e.dataTransfer)) osFileDragActiveRef.current = true;
    };
    const clearOsDrag = () => {
      osFileDragActiveRef.current = false;
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('drop', clearOsDrag);
    window.addEventListener('dragend', clearOsDrag);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('drop', clearOsDrag);
      window.removeEventListener('dragend', clearOsDrag);
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender) setFileDragHighlight(false);
  }, [shouldRender]);

  const appendFiles = useCallback(
    async (selectedFiles: File[]) => {
      if (selectedFiles.length === 0) return;
      if (!validateFilesForDraft(selectedFiles, showToastError)) return;

      setAppendBusy(true);
      setAppendProgress(null);
      try {
        const enriched: ProjectFileDraftItem[] = [];
        const total = selectedFiles.length;
        for (let i = 0; i < selectedFiles.length; i++) {
          const f = selectedFiles[i]!;
          const item: ProjectFileDraftItem = { file: f };
          const label = total > 1 ? `${f.name} (${i + 1} из ${total})` : f.name;
          if (isSupportedProjectMetricsFile(f)) {
            setAppendProgress({
              headline: 'Подсчёт авторских листов',
              fileName: label,
              percent: null,
            });
            const r = await tryComputeAuthorSheetsAndPagesFromFile(f);
            if (!r.ok) {
              showToastError(`Не удалось вычислить АЛ/страницы для «${f.name}»: ${r.errorMessage}`);
              item.file_metrics_unavailable = true;
            } else {
              item.file_author_sheets = r.metrics.authorSheets;
              item.file_pages = r.metrics.pages;
            }
          }
          enriched.push(item);
        }
        onFilesChange([...enriched, ...files]);
      } finally {
        setAppendProgress(null);
        setAppendBusy(false);
      }
    },
    [files, onFilesChange, showToastError]
  );

  const patchDraftAt = useCallback(
    (index: number, patch: Partial<ProjectFileDraftItem>) => {
      onFilesChange(files.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
    },
    [files, onFilesChange]
  );

  const renameStemSplit = useMemo(() => {
    if (renameDraftIndex === null) return { stem: '', suffix: '' };
    const entry = files[renameDraftIndex];
    if (!entry) return { stem: '', suffix: '' };
    return splitFileNameStemAndSuffix(entry.file.name);
  }, [renameDraftIndex, files]);

  useEffect(() => {
    if (!shouldRender) setRenameDraftIndex(null);
  }, [shouldRender]);

  const handleRemoveAt = useCallback(
    (index: number) => {
      onFilesChange(files.filter((_, i) => i !== index));
    },
    [files, onFilesChange]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (appendBusy) return;
      const selectedFiles = Array.from(e.target.files ?? []);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await appendFiles(selectedFiles);
    },
    [appendFiles, appendBusy]
  );

  const handleFilesBodyDragOver = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      if (appendBusy) return;
      if (!dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      setFileDragHighlight(true);
    },
    [appendBusy]
  );

  const handleFilesBodyDragLeave = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      if (!dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      if (!fileDropBodyRef.current?.contains(e.relatedTarget as Node)) {
        setFileDragHighlight(false);
      }
    },
    []
  );

  const handleFilesBodyDrop = useCallback(
    async (e: ReactDragEvent<HTMLDivElement>) => {
      if (appendBusy) return;
      if (!dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      setFileDragHighlight(false);
      await appendFiles(Array.from(e.dataTransfer.files));
    },
    [appendFiles, appendBusy]
  );

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-files-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-files-popover-title"
        >
      <div className="project-files-popover__header">
        <h2 id="project-files-popover-title" className="project-files-popover__title">
          Файлы: {titleName || 'Проект'}
        </h2>
        <div className="project-files-popover__header-actions">
          <ProjectFilesPopoverTypeFilter
            state={typeFilter.state}
            setTypeToggle={typeFilter.setTypeToggle}
            setUntypedToggle={typeFilter.setUntypedToggle}
            isActive={typeFilter.isActive}
          />
          <IconButton
            aria-label="Закрыть"
            onClick={requestClose}
            size="m"
            iconSize={20}
            variant="danger"
            className="project-files-popover__header-action project-files-popover__close"
            disabled={appendBusy}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <div
        ref={fileDropBodyRef}
        className="project-files-popover__body"
        onDragOver={handleFilesBodyDragOver}
        onDragLeave={handleFilesBodyDragLeave}
        onDrop={handleFilesBodyDrop}
      >
        <OverlayScrollArea
          className="project-files-popover__scroll"
          osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
        >
          <div className="project-files-popover__scroll-pad">
            <ul className="project-files-popover__list">
              {files.length === 0 ? (
                <li className="project-files-popover__empty">
                  <i className="fa-regular fa-file-pdf project-files-popover__empty-icon" aria-hidden="true" />
                  <p>Нет прикреплённых файлов</p>
                </li>
              ) : filesFiltered.length === 0 ? (
                <li className="project-files-popover__empty">
                  <i className="fa-regular fa-file-pdf project-files-popover__empty-icon" aria-hidden="true" />
                  <p>Нет файлов по выбранному фильтру</p>
                </li>
              ) : (
                filesFiltered.map((entry) => {
                  const index = files.indexOf(entry);
                  return (
                    <ProjectFilesPopoverLocalDraftRow
                      key={`draft-${index}-${entry.file.name}-${entry.file.size}-${entry.file.lastModified}-${entry.file_type ?? ''}`}
                      item={entry}
                      onPatchDraft={(patch) => patchDraftAt(index, patch)}
                      onRemove={() => handleRemoveAt(index)}
                      onRequestRename={() => setRenameDraftIndex(index)}
                      audioInstanceId={`draft-${index}-${entry.file.name}-${entry.file.size}-${entry.file.lastModified}`}
                      draftActionsLocked={appendBusy}
                      onDraftFileMarkedText={onDraftFileMarkedText}
                    />
                  );
                })
              )}
            </ul>
          </div>
        </OverlayScrollArea>
        {appendProgress ? (
          <ProjectFilesClientWorkProgress
            headline={appendProgress.headline}
            fileName={appendProgress.fileName}
            percent={appendProgress.percent}
          />
        ) : null}
        <div className="project-files-popover__add-btn-wrap">
          <Button
            type="button"
            variant="outlined"
            onClick={handleUploadClick}
            className="project-files-popover__add-btn"
            disabled={appendBusy}
          >
            <AddIcon size={18} />
            {appendBusy ? 'Обработка…' : 'Прикрепить файл'}
          </Button>
        </div>
        {fileDragHighlight ? <DropZoneOverlay /> : null}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-hidden="true"
      />
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return (
    <>
      {shouldRender ? createPortal(popoverContent, document.body) : null}
      <ProjectFileRenameModal
        open={renameDraftIndex !== null && renameDraftIndex >= 0 && renameDraftIndex < files.length}
        onClose={() => setRenameDraftIndex(null)}
        initialStem={renameStemSplit.stem}
        immutableSuffix={renameStemSplit.suffix}
        submitting={false}
        onSave={async (fullFileName) => {
          if (renameDraftIndex === null) return;
          const entry = files[renameDraftIndex];
          if (!entry) return;
          const buf = await entry.file.arrayBuffer();
          const renamed = new File([buf], fullFileName, {
            type: entry.file.type || 'application/octet-stream',
            lastModified: Date.now(),
          });
          patchDraftAt(renameDraftIndex, { file: renamed });
        }}
      />
    </>
  );
}
